import type { PeriodPoint, PeriodsPage, Summary } from "../api";
import { dateRange, duration, full, monthName, pct } from "./format";

/**
 * This week and month to date beside the ones before them.
 *
 * Oldest on the left, current on the right of each group, so a row reads
 * as a trend and the period in progress sits next to the last complete one
 * it will be compared with.
 *
 * **A to-date column is not comparable to a full one.** Four days of a
 * week beside seven always looks like a collapse. So each to-date cell also
 * carries the same elapsed days of the period before — Monday to Thursday
 * against last Monday to Thursday, 1–24 Sep against 1–24 Aug — and the
 * change against *that*. The big number stays the plain total.
 *
 * Changes are shown as text with an arrow, never as green and red: whether
 * a rise is good depends on the row (more leads, yes; more trigger
 * failures, no), and colour would claim to know.
 */
export default function PeriodTable({ page }: { page: PeriodsPage }) {
  const weeks = [...page.weeks].reverse();
  const months = [...page.months].reverse();
  const year = Number(page.today.slice(0, 4));
  const cols = [...weeks, ...months];

  return (
    <div className="ctable ptable">
      <table>
        <thead>
          <tr className="pgroup">
            <th />
            <th colSpan={weeks.length}>Weeks · Mon–Sun</th>
            <th colSpan={months.length} className="gap">
              Months
            </th>
          </tr>
          <tr>
            <th>Metric</th>
            {cols.map((p, i) => (
              <th key={`${p.kind}-${p.offset}`} className={i === weeks.length ? "n gap" : "n"}>
                <div>{heading(p, year)}</div>
                <div className="psub">
                  {p.kind === "week" || p.to_date ? dateRange(p.span.start, p.span.end) : ""}
                </div>
                {p.like_for_like && (
                  <div className="psub">vs {dateRange(p.like_for_like.start, p.like_for_like.end)}</div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => (
            <tr key={row.label}>
              <td title={row.hint}>{row.label}</td>
              {cols.map((p, i) => (
                <Cell key={`${p.kind}-${p.offset}`} p={p} row={row} gap={i === weeks.length} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {cols.some((p) => p.partial_history) && page.history_from && (
        <p className="sub dim">
          <span className="pmark">*</span> Metrics history starts {page.history_from}, so these
          periods are only partly covered — their totals are a floor, not the period's figure.
        </p>
      )}
    </div>
  );
}

function heading(p: PeriodPoint, year: number): string {
  if (p.kind === "week") {
    if (p.offset === 0) return "This week";
    if (p.offset === 1) return "Last week";
    return `${p.offset} weeks ago`;
  }
  return p.to_date ? `${monthName(p.span.start, year)} to date` : monthName(p.span.start, year);
}

function Cell({ p, row, gap }: { p: PeriodPoint; row: RowDef; gap: boolean }) {
  const v = row.value(p.summary);
  const base = p.like_for_like_summary ? row.value(p.like_for_like_summary) : undefined;
  const cls = ["n", gap ? "gap" : "", p.to_date ? "now" : "", p.partial_history ? "thin" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <td className={cls}>
      <div>
        {row.format(v)}
        {p.partial_history && <span className="pmark">*</span>}
      </div>
      {base !== undefined && (
        <div className="psub" title="The same elapsed days of the period before">
          {base === null ? "—" : `${row.format(base)} · ${change(v, base, row.change)}`}
        </div>
      )}
    </td>
  );
}

/** Relative change for a count or average; percentage points for a rate. */
function change(v: number | null, base: number | null, kind: "relative" | "points"): string {
  if (v === null || base === null) return "—";
  if (kind === "points") {
    const d = (v - base) * 100;
    if (Math.abs(d) < 0.05) return "±0pp";
    return `${d > 0 ? "▲" : "▼"}${Math.abs(d).toFixed(1)}pp`;
  }
  if (base === 0) return v === 0 ? "±0%" : "new";
  const d = ((v - base) / base) * 100;
  if (Math.abs(d) < 0.5) return "±0%";
  return `${d > 0 ? "▲" : "▼"}${Math.abs(d).toFixed(0)}%`;
}

interface RowDef {
  label: string;
  hint: string;
  value: (s: Summary) => number | null;
  format: (v: number | null) => string;
  change: "relative" | "points";
}

const count = (v: number | null) => (v === null ? "—" : full(v));

/** The campaign table's columns, plus calls to match the KPI tiles. */
const ROWS: RowDef[] = [
  {
    label: "Leads",
    hint: "Journey groups created in the period",
    value: (s) => s.leads_created,
    format: count,
    change: "relative",
  },
  {
    label: "Calls",
    hint: "Every call row, including ones queued or cancelled before dialling",
    value: (s) => s.calls_total,
    format: count,
    change: "relative",
  },
  {
    label: "Attempts",
    hint: "Calls that actually left our side",
    value: (s) => s.attempts,
    format: count,
    change: "relative",
  },
  {
    label: "Connected",
    hint: "Attempts that reached a human — voicemail excluded",
    value: (s) => s.connected,
    format: count,
    change: "relative",
  },
  {
    label: "Connect rate",
    hint: "Connected ÷ attempts over the whole period — pooled, never an average of daily rates",
    value: (s) => s.connect_rate,
    format: (v) => pct(v),
    change: "points",
  },
  {
    label: "Att / lead",
    hint:
      "Attempts ÷ leads touched per day, summed over the period. A lead dialled on two days counts twice, matching the attempts it is divided into",
    value: (s) => s.attempts_per_lead,
    format: (v) => v?.toFixed(2) ?? "—",
    change: "relative",
  },
  {
    label: "Connected min",
    hint: "Total call length of connected calls, in minutes — voicemail excluded",
    value: (s) => s.talk_seconds / 60,
    format: (v) => (v === null ? "—" : full(Math.round(v))),
    change: "relative",
  },
  {
    label: "Avg talk",
    hint: "Mean over connected calls only",
    value: (s) => s.avg_talk_seconds,
    format: duration,
    change: "relative",
  },
  {
    label: "Trigger failures",
    hint: "Calls that never got out — a platform fault, not customer behaviour",
    value: (s) => s.trigger_failed,
    format: count,
    change: "relative",
  },
];
